//! Dependencies
import { API_URI } from "../../constants/consts";
import { api } from "../../services/apis/configs/axiosConfigs";

//* Reuse the question mapper so the cart's question matches the questions domain
import { mapQuestionResponse } from "../questions/questionsAPI";

//* Types
import { type QuestionCartItem, type AddToCartPayload } from "./types/types";

/**
 * Maps a raw cart item from the API into a QuestionCartItem.
 */
function mapCartItemResponse(data: any): QuestionCartItem {
  return {
    id: String(data.id ?? ""),
    questionId: String(data.questionId ?? data.question_id ?? ""),
    question: mapQuestionResponse(data.question ?? {}),
    organizationId: data.organizationId ?? data.organization_id ?? null,
    createdAt: data.createdAt ? new Date(data.createdAt) : new Date(data.created_at ?? Date.now()),
  };
}

/**
 * @Component questionCartAPI
 * @Description CRUD operations for the persistent question cart.
 */
export const questionCartAPI = {
  /**
   * Fetches every item in the authenticated user's cart.
   */
  getAll: async function (token: string): Promise<QuestionCartItem[]> {
    const url = `${API_URI}/admin/private/question-cart`;

    return api
      .request({
        url,
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const items = response.data?.data ?? [];
        return Array.isArray(items) ? items.map(mapCartItemResponse) : [];
      })
      .catch((error) => {
        console.error("Error fetching question cart:", error);
        throw error;
      });
  },

  /**
   * Adds a question to the cart. Idempotent on the backend.
   */
  add: async function (payload: AddToCartPayload, token: string): Promise<QuestionCartItem> {
    const url = `${API_URI}/admin/private/question-cart`;

    return api
      .request({
        url,
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        data: { question_id: payload.questionId },
      })
      .then((response) => mapCartItemResponse(response.data.data))
      .catch((error) => {
        console.error("Error adding question to cart:", error);
        throw error;
      });
  },

  /**
   * Removes a single item from the cart.
   */
  remove: async function (itemId: string, token: string): Promise<void> {
    const url = `${API_URI}/admin/private/question-cart/${itemId}`;

    return api
      .request({
        url,
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error removing question from cart:", error);
        throw error;
      });
  },

  /**
   * Empties the cart.
   */
  clear: async function (token: string): Promise<void> {
    const url = `${API_URI}/admin/private/question-cart/clear`;

    return api
      .request({
        url,
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error clearing question cart:", error);
        throw error;
      });
  },
};
